"use client";

import { useState, useEffect, KeyboardEvent } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2 } from "lucide-react";

interface PromptInputProps {
  onGenerate: (prompt: string) => void;
  isGenerating: boolean;
  disabled?: boolean;
  initialPrompt?: string;
}

export function PromptInput({
  onGenerate,
  isGenerating,
  disabled,
  initialPrompt,
}: PromptInputProps) {
  const [prompt, setPrompt] = useState("");

  useEffect(() => {
    if (initialPrompt !== undefined) {
      setPrompt(initialPrompt);
    }
  }, [initialPrompt]);

  const handleGenerate = () => {
    if (prompt.trim() && !isGenerating && !disabled) {
      onGenerate(prompt.trim());
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleGenerate();
    }
  };

  return (
    <div className="space-y-3">
      <div className="relative">
        <Textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Describe the image you want to create... (Ctrl+Enter to generate)"
          className="min-h-[120px] resize-none pr-4 text-base bg-secondary/50 border-border focus:border-primary/50"
          disabled={isGenerating || disabled}
          maxLength={2000}
        />
      </div>
      <div className="flex justify-between items-center">
        <span className={`text-sm ${prompt.length > 1800 ? (prompt.length >= 2000 ? 'text-destructive font-medium' : 'text-amber-500') : 'text-muted-foreground'}`}>
          {prompt.length} / 2000 characters
          {prompt.length >= 2000 && ' (limit reached)'}
        </span>
        <Button
          onClick={handleGenerate}
          disabled={!prompt.trim() || isGenerating || disabled}
          size="lg"
          className="gap-2 px-6"
        >
          {isGenerating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              Generate
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
